/**
 * 應用程式主元件 - 後臺管理基底平台
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SystemProvider } from './contexts/SystemContext';
import { AuthProvider } from './contexts/AuthContext';
import { MessageProvider } from './contexts/MessageContext';
import { useSystem } from './contexts/SystemContext';
import { useIsMobile } from './hooks/useIsMobile';
import MaintenancePage from './pages/MaintenancePage';
import LoginPage from './pages/LoginPage';
import OrganizationsPage from './pages/OrganizationsPage';
import SysProfilePage from './pages/SysProfilePage';
import UserRolesPage from './pages/UserRolesPage';
import UsersPage from './pages/UsersPage';
import SystemFunctionsPage from './pages/SystemFunctionsPage';
import RoleRightsPage from './pages/RoleRightsPage';
import UserLogsPage from './pages/UserLogsPage';
import SystemCodesPage from './pages/SystemCodesPage';
import SystemNotificationsPage from './pages/SystemNotificationsPage';
import TenantProfilePage from './pages/TenantProfilePage';
import TenantSysProfilePage from './pages/TenantSysProfilePage';
import TenantUsersPage from './pages/TenantUsersPage';
import HomePage from './pages/HomePage';
import MyProfilePage from './pages/MyProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import EeSchedulerPage from './pages/ee/EeSchedulerPage';
import EeSettingsPage from './pages/ee/EeSettingsPage';
import EeApiRolesPage from './pages/ee/EeApiRolesPage';
import EeApiRoleRightsPage from './pages/ee/EeApiRoleRightsPage';
import EeApiKeysPage from './pages/ee/EeApiKeysPage';
import EeAiProvidersPage from './pages/ee/EeAiProvidersPage';
import EeAiUsagePage from './pages/ee/EeAiUsagePage';
import EeAiReviewPage from './pages/ee/EeAiReviewPage';
import EeMyContactsPage from './pages/EeMyContactsPage';
import EeMyCalendarPage from './pages/EeMyCalendarPage';
import MyDrivePage from './pages/ee/myDrive/MyDrivePage';
import SharedWithMePage from './pages/ee/driveShare/SharedWithMePage';
import LineOaPage from './pages/ee/lineOa/LineOaPage';
import VoicePage from './pages/VoicePage';
import LineCallbackPage from './pages/LineCallbackPage';
import LineLoginCallbackPage from './pages/LineLoginCallbackPage';
import MyDrivePickerPage from './pages/ee/myDrive/MyDrivePickerPage';
import OfficeEditorPage from './pages/ee/myDrive/OfficeEditorPage';
import TenantQuotaPage from './pages/ee/myDrive/admin/TenantQuotaPage';
import TenantDrivePage from './pages/ee/tenantDrive/TenantDrivePage';
import EeWebsitePrototypePage from './pages/ee/websitePrototype/EeWebsitePrototypePage';
import MainLayout from './components/MainLayout';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import PrivacyConsentModal from './components/legal/PrivacyConsentModal';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import appRoutes from './routes/appRoutes';
// Sprint 5-b PWA · standalone 行動版（專屬 MobileLayout·不套 MainLayout sidebar/topnav）
import MobileLayout from './pages/ee/pwa/layout/MobileLayout';
import PWAHomePage from './pages/ee/pwa/PWAHomePage';
import PWACalendarPage from './pages/ee/pwa/PWACalendarPage';
import VoiceCreatePage from './pages/ee/pwa/VoiceCreatePage';
import MeetingRoomPage from './pages/ee/pwa/MeetingRoomPage';
import MinutesPage from './pages/ee/pwa/MinutesPage';
import UpcomingMeetingsPage from './pages/ee/pwa/UpcomingMeetingsPage';
import MinutesHistoryPage from './pages/ee/pwa/MinutesHistoryPage';
import OfficeMinutesListPage from './pages/ee/pwa/OfficeMinutesListPage';
import OfficeMinutesDetailPage from './pages/ee/pwa/OfficeMinutesDetailPage';
import OfficePwaMeetingsListPage from './pages/ee/pwa/OfficePwaMeetingsListPage';
import OfficePwaRoomPage from './pages/ee/pwa/OfficePwaRoomPage';
import PWAMyResumePage from './pages/ee/pwa/PWAMyResumePage';
// Sprint 5-d Hotfix · AI 對話 Claude-style 全螢幕（專屬 AIChatLayout·不套 MainLayout）
import AIChatLayout from './pages/ee/aiChat/AIChatLayout';
import ChatPage from './pages/ee/aiChat/ChatPage';

/**
 * 應用程式路由
 * 根據系統維護狀態決定顯示內容
 */
// 根目錄依裝置導向：手機 / 平板 → 行動版首頁·桌機 → 桌機首頁。
// 只影響根目錄 /（含 catch-all 導回 / 的情況）；明確輸入 /home 仍進桌機（平板想用桌機版可以）。
// document.title 由 SystemContext 統一設定（tenantTitle→system_name→sys_title），不再用 useDocumentTitle。
const RootRedirect: React.FC = () => {
  const isMobile = useIsMobile();
  return <Navigate to={isMobile ? '/ee_pwa_home' : '/home'} replace />;
};

const AppRoutes: React.FC = () => {
  const { isService, isLoading } = useSystem();

  // 載入中
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        系統載入中...
      </div>
    );
  }

  // 系統維護中
  if (!isService) {
    return <MaintenancePage />;
  }

  // 正常服務
  return (
    <ErrorBoundary>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/line-login-callback" element={<LineLoginCallbackPage />} />   {/* #24 Phase C1：以 LINE 登入 · 未登入即用 */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      {/* Task-33 3.2b：線上 Office 編輯（新分頁，無 MainLayout；token 於頁內換發，iframe embed Collabora）*/}
      <Route path="/office/:fileObjectId" element={<OfficeEditorPage />} />

      {/* my_drive picker popup（Task-31 PR 5）：需登入，但無 MainLayout shell（只留 FilePicker）*/}
      <Route
        path="/ee_my_drive_picker"
        element={
          <PrivateRoute>
            <MyDrivePickerPage />
          </PrivateRoute>
        }
      />

      {/* LINE OAuth callback landing（#24 Phase B）：需登入，無 MainLayout shell */}
      <Route
        path="/line-callback"
        element={
          <PrivateRoute>
            <LineCallbackPage />
          </PrivateRoute>
        }
      />

      {/* ===== Sprint 5-b PWA · standalone 行動版（專屬 MobileLayout·無 MainLayout shell）===== */}
      <Route
        element={
          <PrivateRoute>
            <MobileLayout />
          </PrivateRoute>
        }
      >
        <Route path="ee_pwa_home" element={<PWAHomePage />} />
        <Route path="ee_pwa_calendar" element={<PWACalendarPage />} />
        <Route path="ee_pwa_calendar/voice-create" element={<VoiceCreatePage />} />
        {/* 個人行事曆語音會議（Sprint 5-b·非公司會議室）·保留在行事曆入口內 */}
        <Route path="ee_pwa_meetings" element={<UpcomingMeetingsPage />} />
        <Route path="ee_pwa_meetings/history" element={<MinutesHistoryPage />} />
        <Route path="ee_pwa_meetings/:eventId" element={<MeetingRoomPage />} />
        <Route path="ee_pwa_meetings/:eventId/minutes" element={<MinutesPage />} />
        {/* item 7 · 公司會議室（實體會議·手機「會議室」磚 /ee_pwa_office_meetings） */}
        <Route path="ee_pwa_office_meetings" element={<OfficePwaMeetingsListPage />} />
        <Route path="ee_pwa_office_meetings/:id" element={<OfficePwaRoomPage />} />
        {/* item 4 · 公司會議紀錄（手機「會議紀錄」磚 /ee_pwa_minutes·唯一定義） */}
        <Route path="ee_pwa_minutes" element={<OfficeMinutesListPage />} />
        <Route path="ee_pwa_minutes/:id" element={<OfficeMinutesDetailPage />} />
        {/* 人事模組 v2 Phase 1 · 我的履歷（手機磚 /ee_pwa_resume·mobile_path 由 migration 111 帶出） */}
        <Route path="ee_pwa_resume" element={<PWAMyResumePage />} />
      </Route>

      {/* ===== Sprint 5-d Hotfix · AI 對話 Claude-style 全螢幕（專屬 AIChatLayout·無 MainLayout shell）===== */}
      <Route
        element={
          <PrivateRoute>
            <AIChatLayout />
          </PrivateRoute>
        }
      >
        <Route path="ee_ai_chat" element={<ChatPage />} />
        <Route path="ee_ai_chat/scope/:funcCode" element={<ChatPage />} />
        <Route path="ee_ai_chat/:sessionId" element={<ChatPage />} />
      </Route>

      <Route
        path="/"
        element={
          <PrivateRoute>
            <>
              <PrivacyConsentModal />
              <MainLayout />
            </>
          </PrivateRoute>
        }
      >
        <Route index element={<RootRedirect />} />
        {/* ===== 基底平台路由（Base AP — 請勿修改此區塊）===== */}
        <Route path="home" element={<HomePage />} />
        {/* 系統管理 */}
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="sys_profile" element={<SysProfilePage />} />
        <Route path="user_roles" element={<UserRolesPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="system_functions" element={<SystemFunctionsPage />} />
        <Route path="role_rights" element={<RoleRightsPage />} />
        <Route path="user_logs" element={<UserLogsPage />} />
        <Route path="system_codes" element={<SystemCodesPage />} />
        <Route path="system_notifications" element={<SystemNotificationsPage />} />
        {/* 租戶管理 */}
        <Route path="org_profile" element={<div>組織資料檔案</div>} />
        <Route path="tenant_profile" element={<TenantProfilePage />} />
        <Route path="tenant_sys_profile" element={<TenantSysProfilePage />} />
        <Route path="tenant_users" element={<TenantUsersPage />} />
        {/* 個人化功能 */}
        <Route path="my_profile" element={<MyProfilePage />} />
        <Route path="change_password" element={<ChangePasswordPage />} />
        {/* ===== Enterprise Edition 路由（僅當 app.ee 載入且 license 許可）===== */}
        <Route path="ee_scheduler" element={<EeSchedulerPage />} />
        <Route path="ee_settings" element={<EeSettingsPage />} />
        <Route path="ee_api_roles" element={<EeApiRolesPage />} />
        <Route path="ee_api_role_rights" element={<EeApiRoleRightsPage />} />
        <Route path="ee_api_keys" element={<EeApiKeysPage />} />
        <Route path="ee_ai_providers" element={<EeAiProvidersPage />} />
        <Route path="ee_ai_providers_tenant" element={<EeAiProvidersPage />} />
        <Route path="ee_ai_usage" element={<EeAiUsagePage />} />
        <Route path="ee_ai_review" element={<EeAiReviewPage />} />
        {/* ===== EE 商業模組路由（完整 SOP ceremony）===== */}
        <Route path="ee_my_contacts" element={<EeMyContactsPage />} />
        <Route path="ee_my_calendar" element={<EeMyCalendarPage />} />
        <Route path="ee_my_drive" element={<MyDrivePage />} />
        <Route path="ee_drive_share" element={<SharedWithMePage />} />
        <Route path="ee_mod_my_drive" element={<TenantQuotaPage />} />
        <Route path="ee_tenant_drive" element={<TenantDrivePage />} />   {/* #23 增量 2 */}
        <Route path="ee_line_oa" element={<LineOaPage />} />   {/* #24-B2 WASIR Phase A */}
        <Route path="ee_voice_command" element={<VoicePage />} />   {/* #24 Phase C2 語音管家 */}
        <Route path="ee_website_prototype" element={<EeWebsitePrototypePage />} />   {/* WASIR CMS 網站雛型 */}

        {/* ===== 應用專案路由（自動載入 routes/appRoutes.tsx）===== */}
        {appRoutes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
};

/**
 * 應用程式主元件
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <SystemProvider>
        <MessageProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </MessageProvider>
      </SystemProvider>
    </BrowserRouter>
  );
};

export default App;
